/**
 * <s-reveal> — scroll reveal for the storefront.
 *
 * Content is visible by default. Only elements that start BELOW the first
 * screen are hidden, only on the top-level page (never inside preview frames), and only once this script is running, so nothing above
 * the fold can flicker and nothing can stay hidden if JavaScript, the
 * IntersectionObserver, or the animation never runs. Skipped entirely for
 * reduced-motion users and in the theme editor, where preview frames may not
 * scroll or animate.
 *
 * Targets come from the element's `data-targets` selector list.
 */
class SReveal extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;
    if (window.Shopify && window.Shopify.designMode) return;
    // Canvas and editor previews render the page inside frames that may never scroll,
    // so anything hidden there would stay hidden. Only animate the real, top-level page.
    try {
      if (window.self !== window.top) return;
    } catch (error) {
      return;
    }

    const selector = this.dataset.targets;
    if (!selector) return;

    const items = Array.from(document.querySelectorAll(selector));
    const itemSet = new Set(items);
    const foldLine = window.innerHeight * 0.9;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.dataset.reveal = 'shown';
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    for (const element of items) {
      if (element.getBoundingClientRect().top < foldLine) continue;

      // Siblings in the same row cascade in 80ms apart, capped at four steps.
      const parent = element.parentElement;
      const siblings = parent ? Array.from(parent.children).filter((child) => itemSet.has(child)) : [];
      const position = Math.max(siblings.indexOf(element), 0);
      element.style.setProperty('--reveal-delay', `${(position % 4) * 80}ms`);

      element.dataset.reveal = 'hidden';
      observer.observe(element);
    }

    // Restored-from-cache pages show everything rather than replaying hidden states.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      for (const element of items) element.dataset.reveal = 'shown';
    });
  }
}

if (!customElements.get('s-reveal')) customElements.define('s-reveal', SReveal);
